import React from "react";
import {
  OUTCOME_OPTIONS,
  STAY_TYPE_OPTIONS,
  URGENCY_OPTIONS,
  CALLER_TYPE_OPTIONS,
  CHAT_OUTCOME_OPTIONS,
  CHAT_STAY_TYPE_OPTIONS,
  DEFAULT_INTEL_FILTERS,
  activeIntelFilterCount,
  labelFor,
} from "./conversationIntel";

// Phase 7 filter controls, shared by the admin and client pages.
//
// THE LAST ROW IS RAGGED ON VOICE, deliberately. Seven controls in two columns
// leave one alone on row four. The alternatives were worse: spanning it across
// both columns makes one filter look more important than the other six, and
// moving the Clear button up into the spare cell would put an ACTION inside a
// grid of controls — and only on voice, since chat's six fill their rows exactly.
// Clear sits below the grid on both pages, which is the same place on both.
//
// Voice mounts six filters plus caller type. CHAT MOUNTS FIVE plus caller type:
// the after-hours control is NEVER RENDERED, because a website chat at 9pm is not
// remarkable the way an answered phone call is. It is not disabled and not shown
// empty — on this modality it does not exist.
//
// The outcome and stay-type vocabularies differ per channel too. Both lists are
// taken from what the data actually contains, so no control offers a value that
// would match nothing.
//
// The caller-type control is the plan's "visible and removable" guest default:
// it renders pre-set to `guest` like any other control, so a reader can see that
// non-guest conversations are being hidden and can set it to "All". It is
// deliberately NOT a hidden predicate applied before the list is built.

// `justify-end` IS THE ALIGNMENT FIX, and it is why no label needs a fixed
// height. Grid items stretch to their row, so pushing the select to the bottom of
// its own cell lands every select in a row on the same baseline — whatever the
// labels above them do. A label that wraps makes its ROW taller; it can no longer
// shove one select 15px below its neighbour.
//
// The alternative was a fixed label height, which buys the same alignment by
// clipping any label that does not fit. That trades a visible stagger for an
// invisible truncation, which is the worse of the two.
const Select = ({ id, label, value, onChange, children }) => (
  <label htmlFor={id} className="flex flex-col justify-end gap-1 min-w-0">
    {/* `leading-tight`, not the default 1.5: a 10px label was reserving a
        15px line box, and three or four of those is a row of conversations.
        Not `leading-none` — at this size that clips the descender on "y". */}
    <span className="text-[10px] font-semibold uppercase tracking-wider leading-tight text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm max-md:min-h-[44px] rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-2.5 py-1.5"
    >
      {children}
    </select>
  </label>
);

const enumOptions = (values) =>
  values.map((v) => (
    <option key={v} value={v}>
      {labelFor(v)}
    </option>
  ));

const yesNoOptions = [
  <option key="yes" value="yes">Yes</option>,
  <option key="no" value="no">No</option>,
];

const ConversationIntelFilters = ({ filters, onChange, onClear, modality = "voice" }) => {
  const isChat = modality === "chat";
  const f = { ...DEFAULT_INTEL_FILTERS, ...(filters || {}) };
  const set = (key) => (value) => onChange({ ...f, [key]: value });
  const activeCount = activeIntelFilterCount(f);
  const outcomes = isChat ? CHAT_OUTCOME_OPTIONS : OUTCOME_OPTIONS;
  const stayTypes = isChat ? CHAT_STAY_TYPE_OPTIONS : STAY_TYPE_OPTIONS;
  const idFor = (name) => `${isChat ? "ci" : "vi"}-${name}`;

  return (
    <div className="flex flex-col gap-2 pt-3" data-testid="intel-filters">
      {/* TWO COLUMNS, FIXED — NO `sm:` OR `lg:` ESCALATION.
          
          `sm:`/`lg:` are VIEWPORT queries and this block never sees the viewport.
          §2.1 pins the rail at 320–360px at every width above `md`, so a wide
          screen was escalating to FOUR columns inside a 360px box and handing
          each select 78px — for options that read "Transferred to human". The
          same container-vs-viewport mistake as the detail pane's label ruler.

          Measured before the change, chat at a 1600 viewport: rail 360px, four
          columns of 77.75px, and the second row split into two because "Needs
          follow-up" wrapped to two lines and pushed its select 15px below "Has
          barrier". Two columns give ~175px a cell, which is what the option text
          actually needs.

          THE COST IS HEIGHT and it is real: six filters go from two ragged rows
          to three even ones, and voice's seven to four. That is stated rather
          than hidden — the rail's job is the list, and every row here costs a
          row of conversations. */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-3">
        <Select id={idFor("outcome")} label="Outcome" value={f.outcome} onChange={set("outcome")}>
          <option value="all">All</option>
          {enumOptions(outcomes)}
        </Select>
        <Select id={idFor("stay-type")} label="Stay type" value={f.stayType} onChange={set("stayType")}>
          <option value="all">All</option>
          {enumOptions(stayTypes)}
        </Select>
        <Select id={idFor("urgency")} label="Urgency" value={f.urgency} onChange={set("urgency")}>
          <option value="all">All</option>
          {enumOptions(URGENCY_OPTIONS)}
        </Select>
        <Select id={idFor("caller-type")} label={isChat ? "Visitor type" : "Caller type"} value={f.callerType} onChange={set("callerType")}>
          <option value="all">All</option>
          {enumOptions(CALLER_TYPE_OPTIONS)}
        </Select>
        <Select id={idFor("followup")} label="Follow-up" value={f.needsFollowup} onChange={set("needsFollowup")}>
          <option value="all">All</option>
          {yesNoOptions}
        </Select>
        <Select id={idFor("barrier")} label="Has barrier" value={f.hasBarrier} onChange={set("hasBarrier")}>
          <option value="all">All</option>
          {yesNoOptions}
        </Select>
        {!isChat && (
          <Select id={idFor("after-hours")} label="After hours" value={f.afterHours} onChange={set("afterHours")}>
            <option value="all">All</option>
            {yesNoOptions}
          </Select>
        )}
      </div>
      {/* The clear only renders when a handler is given, so a host that owns its
          own clear affordance (the ConversationRail does) can suppress this one by
          not passing onClear — no duplicate clear button. The Project pages pass it
          and keep theirs. */}
      {activeCount > 0 && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="self-start inline-flex items-center min-h-[44px] text-xs font-medium gi-link transition-colors no-underline"
        >
          Clear {activeCount} conversation {activeCount === 1 ? "filter" : "filters"}
        </button>
      )}
    </div>
  );
};

export default ConversationIntelFilters;
